import { useEffect, useState } from "react";
import { downloadDossier, fetchDebrief, generateDebrief } from "../lib/api";
import { Lock, Loader2, Download } from "lucide-react";

export default function Debrief() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchDebrief().then(setData).catch(() => {});
  }, []);

  const run = async () => {
    setBusy(true);
    setErr("");
    try {
      const d = await generateDebrief();
      setData((s) => ({ ...s, debrief: d, gate_open: true }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Synthesis failed");
    } finally {
      setBusy(false);
    }
  };

  const d = data?.debrief;

  return (
    <div
      data-testid="debrief-view-container"
      className="grain thin-scroll relative min-h-0 flex-1 overflow-y-auto bg-[var(--void)]"
    >
      <div className="mx-auto max-w-3xl px-8 py-16 sm:px-12">
        <p className="label">Subject debrief</p>
        <h1 className="mt-4 font-display text-4xl leading-[1.02] tracking-tight sm:text-5xl">
          How other people
          <br />
          <span className="italic text-zinc-400">actually move you.</span>
        </h1>
        <div className="hair my-10 max-w-xs" />

        {!data?.gate_open && (
          <div className="panel flex items-start gap-4 px-6 py-6">
            <Lock size={15} className="mt-0.5 shrink-0 text-zinc-500" strokeWidth={1.4} />
            <div>
              <p className="label">Reveal gate sealed</p>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-zinc-400">
                Your results are held until the instrument can predict — in advance — whether your
                next answer will be candid or managed, to 99.73% confidence. Honesty signal is
                currently{" "}
                <span className="font-data text-zinc-100">
                  {Math.round(data?.honesty_signal || 0)}
                </span>{" "}
                and {data?.honesty_trend || "establishing"}. Keep going.
              </p>
            </div>
          </div>
        )}

        {data?.gate_open && !d && (
          <div className="panel px-6 py-6">
            <p className="label">Gate open</p>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              The map is trustworthy. Run synthesis to produce your behavioural debrief.
            </p>
            <button
              onClick={run}
              disabled={busy}
              data-testid="debrief-generate-btn"
              className="btn-stark mt-5 inline-flex items-center gap-2"
            >
              {busy && <Loader2 size={11} className="animate-spin" />}
              {busy ? "Synthesising" : "Run synthesis"}
            </button>
            {err && <p className="mt-3 font-data text-[11px] text-zinc-300">— {err}</p>}
          </div>
        )}

        {d && (
          <div className="space-y-14">
            {data?.receipt_email && (
              <p
                data-testid="debrief-receipt-email"
                className="border-l border-[var(--line-hi)] pl-5 font-data text-[11px] leading-relaxed text-zinc-500"
              >
                {data.receipt_email.sent_at
                  ? `Your signed consent receipt ${data.receipt_email.receipt_id || ""} was emailed to ${data.receipt_email.to} when your results were released.`
                  : data.receipt_email.error
                    ? `We could not email your receipt to ${data.receipt_email.to}. You can still download it from the privacy page.`
                    : `Your signed consent receipt is on its way to ${data.receipt_email.to}.`}
              </p>
            )}

            <section className="tick-in">
              <p className="font-display text-2xl leading-snug text-zinc-100">{d.headline}</p>
            </section>

            <section className="tick-in">
              <p className="label">Honesty read</p>
              <p className="mt-3 text-sm leading-[1.8] text-zinc-300">{d.honesty_read}</p>
              <div className="mt-5 flex items-center gap-3">
                <span className="font-data text-3xl tabular-nums text-zinc-100">
                  {Math.round(data.honesty_signal)}
                </span>
                <div className="h-[3px] w-40 bg-[var(--line)]">
                  <div className="h-full bg-zinc-100" style={{ width: `${data.honesty_signal}%` }} />
                </div>
                <span className="label">of 100 · {data.honesty_trend}</span>
              </div>
            </section>

            <section className="tick-in">
              <p className="label">Where your mass sits</p>
              <p className="mt-3 text-sm leading-[1.8] text-zinc-300">{d.mass_distribution}</p>
            </section>

            {(data.cohort_grounding || []).length > 0 && (
              <section className="tick-in" data-testid="debrief-cohort-list">
                <p className="label">Your stated needs vs your cohort</p>
                <p className="mt-2 font-data text-[10px] text-zinc-600">
                  checked against live population statistics, not recollection
                </p>
                <div className="mt-5 space-y-5">
                  {data.cohort_grounding.map((g) => (
                    <div key={g.query || g.category} className="border-l border-[var(--line-hi)] pl-5">
                      <div className="flex flex-wrap items-baseline gap-3">
                        <span className="font-data text-[10px] uppercase tracking-[0.18em] text-zinc-100">
                          {g.verdict}
                        </span>
                        <span className="label">{g.confidence} confidence</span>
                      </div>
                      <p className="mt-2 text-sm text-zinc-200">“{g.stated_need}”</p>
                      {g.cohort_rate && (
                        <p className="mt-1 font-data text-[11px] text-zinc-400">{g.cohort_rate}</p>
                      )}
                      <p className="mt-2 text-xs leading-relaxed text-zinc-500">{g.basis}</p>
                      {g.sources?.length > 0 && (
                        <p className="mt-2 flex flex-wrap gap-x-4">
                          {g.sources.map((s) => (
                            <a
                              key={s.url}
                              href={s.url}
                              target="_blank"
                              rel="noreferrer"
                              className="font-data text-[10px] text-zinc-600 underline decoration-zinc-800 hover:text-zinc-300"
                            >
                              {s.title || s.url}
                            </a>
                          ))}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="tick-in" data-testid="debrief-influence-list">
              <p className="label">Nodes of influence</p>
              <div className="mt-6 space-y-8">
                {(d.influence_nodes || []).map((n, i) => (
                  <article
                    key={n.id || n.name || n.title}
                    className={`border-l-2 pl-5 ${
                      n.valence === "corrosive" ? "border-zinc-200" : "border-zinc-600"
                    }`}
                  >
                    <div className="flex items-baseline gap-3">
                      <span className="font-data text-[10px] text-zinc-600">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="font-display text-xl leading-tight">{n.title}</h3>
                      <span className="label ml-auto">{n.valence}</span>
                    </div>
                    <dl className="mt-4 space-y-3">
                      {[
                        ["Trigger", n.trigger],
                        ["Circuit", n.circuit],
                        ["Your response", n.response],
                        ["Business cost", n.business_cost],
                        ["Adjustment", n.adjustment],
                      ].map(([k, v]) => (
                        <div key={k} className="grid grid-cols-[120px_1fr] gap-4">
                          <dt className="label pt-0.5">{k}</dt>
                          <dd className="text-sm leading-relaxed text-zinc-300">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </article>
                ))}
              </div>
            </section>

            {(d.facet_reveal || []).length > 0 && (
              <section className="tick-in" data-testid="debrief-facet-reveal">
                <p className="label">In your own words</p>
                <p className="mt-3 max-w-2xl text-xs leading-relaxed text-zinc-500">
                  These are the needs you named yourself, kept in your wording rather than
                  translated into ours. A <em>standing</em> one came back often enough and strongly
                  enough to be treated as a fixed part of your map; an <em>emerging</em> one has
                  shown itself but has not yet settled.
                </p>
                <div className="mt-6 space-y-7">
                  {d.facet_reveal.map((f, i) => (
                    <article
                      key={f.name || i}
                      data-testid={`debrief-facet-${i}`}
                      className="border-l-2 border-[var(--line-hi)] pl-5"
                    >
                      <div className="flex flex-wrap items-baseline gap-3">
                        <h3 className="font-display text-xl leading-tight">“{f.name}”</h3>
                        <span className="label">{f.parent_name}</span>
                        <span
                          className={`label ml-auto ${
                            f.standing === "standing" ? "!text-zinc-200" : ""
                          }`}
                        >
                          {f.standing}
                        </span>
                      </div>
                      <p className="mt-3 text-sm leading-relaxed text-zinc-300">{f.what_it_is}</p>
                      {f.where_it_shows && (
                        <p className="mt-2 text-sm leading-relaxed text-zinc-500">
                          Where it shows: {f.where_it_shows}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            )}

            <section className="tick-in grid gap-10 sm:grid-cols-2">
              <div>
                <p className="label">Destabilises you</p>
                <ul className="mt-3 space-y-2">
                  {(d.destabilisers || []).map((x) => (
                    <li key={x} className="border-b border-[var(--line)] pb-2 text-sm text-zinc-300">
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="label">Steadies you</p>
                <ul className="mt-3 space-y-2">
                  {(d.stabilisers || []).map((x) => (
                    <li key={x} className="border-b border-[var(--line)] pb-2 text-sm text-zinc-300">
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="tick-in">
              <p className="label">Blind spot</p>
              <p className="mt-3 border-l-2 border-zinc-100 pl-5 text-base leading-[1.8] text-zinc-100">
                {d.blind_spot}
              </p>
            </section>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={async () => {
                  setPdfBusy(true);
                  try {
                    await downloadDossier();
                  } catch {
                    setErr("Could not typeset the dossier.");
                  } finally {
                    setPdfBusy(false);
                  }
                }}
                disabled={pdfBusy}
                data-testid="debrief-export-btn"
                className="btn-stark flex items-center gap-2"
              >
                {pdfBusy ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                {pdfBusy ? "Typesetting" : "Download dossier (PDF)"}
              </button>
              <button
                onClick={() => window.print()}
                data-testid="debrief-print-btn"
                className="btn-stark"
              >
                Print
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
